import { useState } from "react";
import {
  Box, Drawer, Typography, Divider, Button, Chip, IconButton, Tooltip, styled,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import FlagIcon from "@mui/icons-material/Flag";
import GavelIcon from "@mui/icons-material/Gavel";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import { format } from "date-fns";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { formatCurrency } from "@/utils/format-currency";
import { formatLabel } from "@/utils/format-label";
import { usePermission } from "@/hooks/use-permissions";
import { CAPABILITIES } from "@/config/permissions";
import { useManualSettlement } from "@/hooks/use-manual-settlement";
import { showToast } from "@/redux/actions/toast-actions";
import { getTransactionSettlementEligibility } from "@/utils/manual-settlement";
import ManualSettlementDialog from "@/components/settlement/manual-settlement-dialog";

const DrawerContent = styled(Box)(({ theme }) => ({
  width: "33vw",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  padding: theme.spacing(3),
  overflowY: "auto",
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: "0.75rem",
  fontWeight: 600,
  textTransform: "uppercase",
  color: theme.palette.text.secondary,
  letterSpacing: "0.05em",
  marginBottom: theme.spacing(1),
}));

const InfoRow = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  paddingBlock: theme.spacing(1.2),
  borderBottom: `1px solid ${theme.palette.divider}`,
  "&:last-of-type": {
    borderBottom: "none",
  },
}));

const InfoLabel = styled(Typography)(({ theme }) => ({
  fontSize: "0.875rem",
  color: theme.palette.text.secondary,
  flex: 1,
}));

const InfoValue = styled(Typography)(({ theme }) => ({
  fontSize: "0.875rem",
  fontWeight: 500,
  color: theme.palette.text.primary,
  flex: 2,
  textAlign: "right",
  wordBreak: "break-word",
}));

export const StatusChip = styled(Chip)(({ status }) => ({
  borderRadius: "999px",
  fontWeight: 500,
  fontSize: "0.8rem",
  backgroundColor:
    status === "SUCCESSFUL"
      ? "#f0fdf4"
      : status === "PENDING"
        ? "#fffbeb"
        : "#fef2f2",
  color:
    status === "SUCCESSFUL"
      ? "#15803d"
      : status === "PENDING"
        ? "#d97706"
        : "#dc2626",
}));

const formatDate = (dateStr) => {
  if (!dateStr) return "N/A";
  try {
    return format(new Date(dateStr), "MMM d, yyyy h:mm a");
  } catch {
    return dateStr;
  }
};

export function TransactionSheet({ transaction, open, onClose }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const canSettleManually = usePermission(CAPABILITIES.SETTLEMENT_MANUAL);
  const { eligible, reason } = getTransactionSettlementEligibility(transaction);
  const settlementMutation = useManualSettlement();
  const [settleOpen, setSettleOpen] = useState(false);

  const handleCreateDispute = () => {
    onClose();
    navigate("/disputes/chargebacks", {
      state: { createDisputeTransaction: transaction },
    });
  };
  
  const handleFlag = () => {
    // TODO
    // console.log("flag transaction", transaction?.paymentMetaData.currency);
  };

  const handleSettle = (reference) => {
    settlementMutation.mutate(
      { paymentRefNo: reference },
      {
        onSuccess: (res) => {
          dispatch(showToast(res?.message ?? "Settlement processed", "success"));
          setSettleOpen(false);
        },
        onError: (err) =>
          dispatch(showToast(err?.message ?? "Settlement could not be processed", "error")),
      },
    );
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      SlotProps={{
        backdrop: {
          onClick: onClose,
        },
      }}
    >
      <DrawerContent>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Box>
            <Typography variant="h6" fontWeight={600}>
              Transaction Details
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Ref: {transaction?.refNo ?? "N/A"}
            </Typography>
          </Box>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
            p: 2,
            borderRadius: 2,
            backgroundColor: "grey.50",
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          <Box>
            <Typography variant="caption" color="text.secondary">
              Amount
            </Typography>
            <Typography variant="h5" fontWeight={700}>
              {formatCurrency(transaction?.amount)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Fee: {formatCurrency(transaction?.fee)}
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1,
              alignItems: "flex-end",
            }}
          >
            <StatusChip
              label={formatLabel(transaction?.status) ?? "N/A"}
              status={transaction?.status}
              size="small"
            />
            <StatusChip
              label={`Settlement: ${
                formatLabel(transaction?.settlementStatus) ?? "N/A"
              }`}
              status={transaction?.settlementStatus}
              size="small"
            />
          </Box>
        </Box>

        <SectionTitle>Customer Information</SectionTitle>
        <Box sx={{ mb: 3 }}>
          <InfoRow>
            <InfoLabel>Name</InfoLabel>
            <InfoValue>{transaction?.customerName ?? "N/A"}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>Email</InfoLabel>
            <InfoValue>{transaction?.customerEmail ?? "N/A"}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>Phone</InfoLabel>
            <InfoValue>{transaction?.customerPhone ?? "N/A"}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>IP Address</InfoLabel>
            <InfoValue>{transaction?.customerIpAddress ?? "N/A"}</InfoValue>
          </InfoRow>
        </Box>

        <Divider sx={{ mb: 3 }} />

        <SectionTitle>Transaction Information</SectionTitle>
        <Box sx={{ mb: 3 }}>
          <InfoRow>
            <InfoLabel>Channel</InfoLabel>
            <InfoValue>{transaction?.channel ?? "N/A"}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>Mode</InfoLabel>
            <InfoValue>{transaction?.mode ?? "N/A"}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>Processed By</InfoLabel>
            <InfoValue>{transaction?.processedBy ?? "N/A"}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>Merchant</InfoLabel>
            <InfoValue>{transaction?.merchantName ?? "N/A"}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>Merchant ID</InfoLabel>
            <InfoValue>{transaction?.merchantId ?? "N/A"}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>Transaction Date</InfoLabel>
            <InfoValue>{formatDate(transaction?.tranDate)}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>Vendor Date</InfoLabel>
            <InfoValue>{formatDate(transaction?.vendorDate)}</InfoValue>
          </InfoRow>
        </Box>

        <Divider sx={{ mb: 3 }} />

        <SectionTitle>Payment Metadata</SectionTitle>
        <Box sx={{ mb: 3 }}>
          <InfoRow>
            <InfoLabel>Description</InfoLabel>
            <InfoValue>{transaction?.description}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>Currency</InfoLabel>
            <InfoValue>{transaction?.paymentMetaData?.currency}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>Payment Link ID</InfoLabel>
            <InfoValue>{transaction?.paymentLinkId}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>Recurrent Payment</InfoLabel>
            <InfoValue>
              {transaction?.recurrentPayment ? "Yes" : "No"}
            </InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>Public Key</InfoLabel>
            <InfoValue sx={{ fontSize: "0.75rem", wordBreak: "break-all" }}>
              {transaction?.paymentMetaData?.wayaPublicKey}
            </InfoValue>
          </InfoRow>
        </Box>

        <Box sx={{ flex: 1 }} />

        <Divider sx={{ mb: 2 }} />

        <Box
          sx={{
            display: "flex",
            gap: 1.5,
            justifyContent: "flex-end",
            flexWrap: "wrap",
          }}
        >
          <Button
            variant="outlined"
            onClick={onClose}
            sx={{ borderRadius: "999px", px: 3, minHeight: 42 }}
          >
            Close
          </Button>

          <Button
            variant="outlined"
            color="warning"
            startIcon={<FlagIcon />}
            onClick={handleFlag}
            sx={{ borderRadius: "999px", px: 3, minHeight: 42 }}
          >
            Flag
          </Button>

          <Button
            variant="outlined"
            startIcon={<GavelIcon />}
            onClick={handleCreateDispute}
            sx={{ borderRadius: "999px", px: 3, minHeight: 42 }}
          >
            Create Dispute
          </Button>

          {/* Hidden entirely without the capability; shown disabled with the
      reason when the transaction itself is not settleable. */}
          {canSettleManually && (
            <Tooltip title={eligible ? "" : reason ?? ""} arrow placement="top">
              <span>
                <Button
                  variant="contained"
                  startIcon={<PaidOutlinedIcon />}
                  disabled={!eligible}
                  onClick={() => setSettleOpen(true)}
                  sx={{ borderRadius: "999px", px: 3, minHeight: 42 }}
                >
                  Settle Manually
                </Button>
              </span>
            </Tooltip>
          )}
        </Box>
      </DrawerContent>

      <ManualSettlementDialog
        open={settleOpen}
        onClose={() => setSettleOpen(false)}
        reference={transaction?.refNo}
        rows={[
          { label: "Reference", value: transaction?.refNo },
          {
            label: "Merchant",
            value: transaction?.merchantName ?? transaction?.merchantId,
          },
          { label: "Customer", value: transaction?.customerName },
          { label: "Amount", value: formatCurrency(transaction?.amount) },
          { label: "Fee", value: formatCurrency(transaction?.fee) },
        ]}
        notice={
          transaction?.sentForSettlement
            ? "This transaction has already been sent for automatic settlement."
            : null
        }
        mutation={settlementMutation}
        onConfirm={handleSettle}
      />
    </Drawer>
  );
}